import {IndexedDBService} from 'go/src/atlas/edison_service_worker/service_worker/util/indexed_db_service';
import type {
  RequestMiddleware,
  ResponseMiddleware,
  RequestMiddlewareFn,
  ResponseMiddlewareFn,
} from './types';

interface ServiceWorkerMiddlewareOpts {
  featureFlagsUrl?: string;
  refreshInterval?: number;
  reportError?: (error: Error, level?: 'error' | 'warning' | 'info') => void;
}

const DEFAULT_FEATURE_FLAGS_URL = '/service_worker_flags.json';
const DEBUG_LOG = false;

/**
 * The `ServiceWorkerMiddleware` class provides a mechanism to register and handle request and response middleware
 * for a service worker. It supports feature flags to conditionally enable or disable middleware functions.
 *
 * See ./README.md for more information.
 */
export class ServiceWorkerMiddleware {
  private requestMiddleware: RequestMiddleware[] = [];
  private responseMiddleware: ResponseMiddleware[] = [];
  private featureFlags: {[key: string]: boolean} = {};
  private featureFlagsUrl: string;
  private refreshInterval: number; // Refresh interval ms
  private indexedDBService: IndexedDBService;
  private reportError: ServiceWorkerMiddlewareOpts['reportError'];

  constructor(opts: ServiceWorkerMiddlewareOpts = {}) {
    const {
      featureFlagsUrl = DEFAULT_FEATURE_FLAGS_URL,
      refreshInterval = 60 * 60 * 1000, // 1 hour
    } = opts;
    this.featureFlagsUrl = featureFlagsUrl;
    this.refreshInterval = refreshInterval;
    this.reportError = opts.reportError || (() => {});
    this.indexedDBService = new IndexedDBService('ServiceWorkerDB', 'featureFlags');

    this.initRefresh();
  }

  async loadFeatureFlags(): Promise<void> {
    try {
      const response = await fetch(this.featureFlagsUrl);
      this.featureFlags = await response.json();
      this.storeFeatureFlags();
    } catch (error) {
      if (DEBUG_LOG) {
        // eslint-disable-next-line no-console
        console.error('[SWM] Failed to load feature flags:', error);
      }
      this.reportError?.(error as Error, 'warning');
      this.retrieveStoredFeatureFlags();
    }
  }

  private initRefresh(): void {
    setInterval(() => this.refreshFeatureFlags(), this.refreshInterval);
  }

  private async refreshFeatureFlags(): Promise<void> {
    if (DEBUG_LOG) {
      // eslint-disable-next-line no-console
      console.log('[SWM] Refreshing feature flags...');
    }
    try {
      await this.loadFeatureFlags();
      if (DEBUG_LOG) {
        // eslint-disable-next-line no-console
        console.log('[SWM] Feature flags refreshed successfully.');
      }
    } catch (error) {}
  }

  private async storeFeatureFlags(): Promise<void> {
    try {
      await this.indexedDBService.storeData('flags', this.featureFlags);
    } catch (error) {
      if (DEBUG_LOG) {
        // eslint-disable-next-line no-console
        console.error('[SWM] Failed to store feature flags:', error);
      }
    }
  }

  private async retrieveStoredFeatureFlags(): Promise<void> {
    try {
      this.featureFlags = await this.indexedDBService.retrieveData('flags');
    } catch (error) {
      if (DEBUG_LOG) {
        // eslint-disable-next-line no-console
        console.error('[SWM] Failed to retrieve stored feature flags:', error);
      }
    }
  }

  /**
   * Registers a request middleware function to be executed for a specific route or all routes.
   *
   * @param routeOrFn - Either a route string or a middleware function.
   * @param fnOrFeatureFlag - Either a middleware function or a feature flag string. Optional.
   * @param featureFlag - A feature flag string. Optional.
   * @returns The current instance of `ServiceWorkerMiddleware`.
   *
   * @throws {Error} If the arguments provided are invalid.
   */
  useRequest(
    routeOrFn: string | RequestMiddlewareFn,
    fnOrFeatureFlag?: RequestMiddlewareFn | string | null,
    featureFlag: string | null = null
  ): ServiceWorkerMiddleware {
    if (typeof routeOrFn === 'function') {
      this.requestMiddleware.push({
        route: null,
        fn: routeOrFn,
        featureFlag: fnOrFeatureFlag as string | null,
      });
    } else if (typeof routeOrFn === 'string' && typeof fnOrFeatureFlag === 'function') {
      this.requestMiddleware.push({route: routeOrFn, fn: fnOrFeatureFlag, featureFlag});
    } else if (typeof routeOrFn === 'string' && typeof fnOrFeatureFlag === 'string') {
      this.requestMiddleware.push({
        route: null,
        fn: routeOrFn as unknown as RequestMiddlewareFn,
        featureFlag: fnOrFeatureFlag,
      });
    } else {
      throw new Error('Invalid request middleware arguments.');
    }
    return this;
  }

  /**
   * Registers a response middleware function to be executed for a specific route or all routes.
   *
   * @param routeOrFn - Either a route string or a middleware function.
   * @param fnOrFeatureFlag - Either a middleware function or a feature flag string. Optional.
   * @param featureFlag - A feature flag string. Optional.
   * @returns The current instance of `ServiceWorkerMiddleware`.
   *
   * @throws {Error} If the arguments provided are invalid.
   */
  useResponse(
    routeOrFn: string | ResponseMiddlewareFn,
    fnOrFeatureFlag?: ResponseMiddlewareFn | string | null,
    featureFlag: string | null = null
  ): ServiceWorkerMiddleware {
    if (typeof routeOrFn === 'function') {
      this.responseMiddleware.push({
        route: null,
        fn: routeOrFn,
        featureFlag: fnOrFeatureFlag as string | null,
      });
    } else if (typeof routeOrFn === 'string' && typeof fnOrFeatureFlag === 'function') {
      this.responseMiddleware.push({route: routeOrFn, fn: fnOrFeatureFlag, featureFlag});
    } else if (typeof routeOrFn === 'string' && typeof fnOrFeatureFlag === 'string') {
      this.responseMiddleware.push({
        route: null,
        fn: routeOrFn as unknown as ResponseMiddlewareFn,
        featureFlag: fnOrFeatureFlag,
      });
    } else {
      throw new Error('Invalid response middleware arguments.');
    }
    return this;
  }

  async handleRequest(request: Request): Promise<Request | Response> {
    let modifiedRequest: Request | Response = request;
    let index = 0;

    const next = async (reqOrRes?: Request | Response): Promise<void> => {
      if (reqOrRes instanceof Response) {
        // next() called with a Response, short-circuit the chain
        modifiedRequest = reqOrRes;
        index = this.requestMiddleware.length; // skip remaining middleware
        return;
      }

      if (reqOrRes instanceof Request) {
        modifiedRequest = reqOrRes;
      }

      if (index < this.requestMiddleware.length) {
        const currentMiddleware = this.requestMiddleware[index++];
        const {route, fn, featureFlag} = currentMiddleware;
        if (
          (!featureFlag || this.featureFlags[featureFlag]) &&
          (!route || request.url.includes(route))
        ) {
          const result = await fn(modifiedRequest as Request, next);

          if (result instanceof Response) {
            // has Response, short-circuit
            modifiedRequest = result;
            index = this.requestMiddleware.length;
          } else if (result instanceof Request) {
            modifiedRequest = result;
            await next();
          }
        } else {
          await next();
        }
      }
    };

    await next();
    return modifiedRequest;
  }

  async handleResponse(
    response: Response,
    request: Request,
    event: {respondWith: (response: Response | Promise<Response>) => void}
  ): Promise<Response> {
    let modifiedResponse = response;
    let index = 0;

    const next = async (res?: Response): Promise<void> => {
      if (res instanceof Response) {
        modifiedResponse = res;
      }
      if (index < this.responseMiddleware.length) {
        const currentMiddleware = this.responseMiddleware[index++];
        const {route, fn, featureFlag} = currentMiddleware;
        if (
          (!featureFlag || this.featureFlags[featureFlag]) &&
          (!route || request.url.includes(route))
        ) {
          const result = await fn(modifiedResponse, request, event, next);
          if (result instanceof Response) {
            modifiedResponse = result;
          }
        } else {
          await next();
        }
      }
    };
    await next();
    return modifiedResponse;
  }

  /**
   * Handles the fetch event by modifying the request and processing the response.
   *
   * @param {FetchEvent} event - The fetch event to handle.
   * @returns {Promise<Response>} A promise that resolves to the response after handling the request and response.
   *
   * @throws {Error} If an error occurs during the fetch operation, the original fetch promise is returned.
   */
  async handleFetchEvent(event: FetchEvent): Promise<Response> {
    let response: Response;
    try {
      const requestResult = await this.handleRequest(event.request);

      // If request middleware returned a Response, use it directly
      if (requestResult instanceof Response) {
        response = requestResult;
      } else {
        // Otherwise, fetch with modified request
        response = await fetch(requestResult);
      }
    } catch (error) {
      if (DEBUG_LOG) {
        // eslint-disable-next-line no-console
        console.error('[SWM] Request middleware error:', error);
      }
      this.reportError?.(error as Error, 'error');

      // If request middleware fails, fall back to original request
      response = await fetch(event.request);
    }

    try {
      return await this.handleResponse(response, event.request, event);
    } catch (error) {
      if (DEBUG_LOG) {
        // eslint-disable-next-line no-console
        console.error('[SWM] Response middleware error:', error);
      }
      this.reportError?.(error as Error, 'error');

      // If response middleware fails, return unmodified response
      return response;
    }
  }
}
